import { useState, useEffect } from "react";
import { Localization } from "@viamap/viamap2-common";
import { GlassInputControl } from "../../components/GlassInput";
import { GlassButton } from "src/components/GlassButtons";
import { AdvancedDragModal, ADModalBody, ADModalFooter } from "src/componentsUtils/AdvancedDragModal";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { YearGrouping } from "./JPBState";

function formatDateInputValue(date: Date | undefined): string {
   if (!date) return "";
   const year = date.getFullYear();
   const month = String(date.getMonth() + 1).padStart(2, "0");
   const day = String(date.getDate()).padStart(2, "0");
   return `${year}-${month}-${day}`;
}

function parseDateInputValue(value: string): Date | null {
   if (!value) return null;
   const parsed = new Date(`${value}T12:00:00`);
   if (Number.isNaN(parsed.getTime())) return null;
   return parsed;
}

export type JPBTimeHorizonWindowParams = {
   calculationDateTo: Date | null;
   yearGrouping: YearGrouping;
   minCalculationDateTo?: Date;
   maxCalculationDateTo?: Date;
   availableYearGroupings?: YearGrouping[];
   onConfirm?: (calculationDateTo: Date | null, yearGrouping: YearGrouping) => void;
};

export function JPBTimeHorizonWindow(props: { managedWindow: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   const params = props.managedWindow.windowParams as JPBTimeHorizonWindowParams | undefined;

   const calculationDateTo = params?.calculationDateTo ?? null;
   const yearGrouping = params?.yearGrouping ?? YearGrouping.ThreeYears;
   const minCalculationDateTo = params?.minCalculationDateTo;
   const maxCalculationDateTo = params?.maxCalculationDateTo;
   const availableYearGroupings = params?.availableYearGroupings;
   const onConfirm = params?.onConfirm;

   const [draftDate, setDraftDate] = useState<Date | null>(calculationDateTo);
   const [draftGrouping, setDraftGrouping] = useState<YearGrouping>(yearGrouping);

   useEffect(() => {
      setDraftDate(calculationDateTo);
      setDraftGrouping(yearGrouping);
   }, [calculationDateTo, yearGrouping]);

   const yearOptions = (availableYearGroupings && availableYearGroupings.length > 0
      ? availableYearGroupings
      : [YearGrouping.OneYear, YearGrouping.TwoYears, YearGrouping.ThreeYears, YearGrouping.FourYears, YearGrouping.FiveYears]
   ).map((value) => ({
      value,
      label: value === YearGrouping.OneYear
         ? Localization.getText("1 year")
         : value === YearGrouping.TwoYears
            ? Localization.getText("2 years")
            : value === YearGrouping.ThreeYears
               ? Localization.getText("3 years")
               : value === YearGrouping.FourYears
                  ? Localization.getText("4 years")
                  : Localization.getText("5 years")
   }));

   const handleClose = () => {
      windowDispatch(closeWindow(props.managedWindow.id));
   };

   const handleConfirm = () => {
      onConfirm?.(draftDate, draftGrouping);
      windowDispatch(closeWindow(props.managedWindow.id));
   };

   return (
      <AdvancedDragModal
         title={Localization.getText("Calculation period")}
         variant="Small"
         window={props.managedWindow}
         onClose={handleClose}
         PosDefault={{
            left: "50%",
            top: "30%",
            transform: "translate(-50%, -50%)",
            width: "360px"
         }}
         className="jpb-time-horizon-modal"
      >
         <ADModalBody>
            <div className="jpb-time-horizon-modal-fields">
               <div className="jpb-time-horizon-modal-field">
                  <label className="GlassInputLabel">{Localization.getText("Calculate date to")}</label>
                  <GlassInputControl>
                     <input
                        type="date"
                        value={formatDateInputValue(draftDate ?? undefined)}
                        min={formatDateInputValue(minCalculationDateTo)}
                        max={formatDateInputValue(maxCalculationDateTo)}
                        onChange={(e) => {
                           const parsed = parseDateInputValue(e.target.value);
                           setDraftDate(parsed);
                        }}
                     />
                  </GlassInputControl>
               </div>
               <div className="jpb-time-horizon-modal-field">
                  <label className="GlassInputLabel">{Localization.getText("Reference period")}</label>
                  <GlassInputControl>
                     <select
                        value={String(draftGrouping)}
                        onChange={(e) => setDraftGrouping(Number(e.target.value) as YearGrouping)}
                     >
                        {yearOptions.map((opt) => (
                           <option key={opt.value} value={String(opt.value)}>
                              {opt.label}
                           </option>
                        ))}
                     </select>
                  </GlassInputControl>
               </div>
            </div>
         </ADModalBody>
         <ADModalFooter>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px" }}>
               <GlassButton onClick={handleClose}>
                  {Localization.getText("Cancel")}
               </GlassButton>
               <GlassButton onClick={handleConfirm}>
                  {Localization.getText("OK")}
               </GlassButton>
            </div>
         </ADModalFooter>
      </AdvancedDragModal>
   );
}
